import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { usePostHog } from 'posthog-js/react';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import { SubscriptionPlans } from '../../components/subscriptions/SubscriptionPlans/SubscriptionPlans';
import TrialStatusCard from '../../components/subscriptions/TrialStatusCard/TrialStatusCard';
import BillingCycleCard from '../../components/subscriptions/BillingCycleCard/BillingCycleCard';
import PlanSwitchModal from '../../components/subscriptions/PlanSwitchModal/PlanSwitchModal';
import { CreditBalance } from '../../components/common/CreditBalance/CreditBalance';
import { CreditHistory } from '../../components/common/CreditHistory';
import Card from '../../components/common/Card/Card';
import Notification from '../../components/common/Notification/Notification';
import { CreditCard } from 'lucide-react';
import { subscriptionsApi } from '../../api/subscriptions';
import { CurrentSubscription, SubscriptionPlan, isInTrial } from '../../types/subscription.types';

const Subscriptions: React.FC = () => {
  const [currentPlanName, setCurrentPlanName] = useState<string | undefined>();
  const [isTrial, setIsTrial] = useState(false);
  const [subscription, setSubscription] = useState<CurrentSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirectingToPlan, setRedirectingToPlan] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info', message: React.ReactNode } | null>(null);
  const [planSwitchTarget, setPlanSwitchTarget] = useState<SubscriptionPlan | null>(null);
  const location = useLocation();
  const posthog = usePostHog();

  const subscriptionPlansRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRedirectingToPlan(false);
  }, []);

  const refreshSubscription = async () => {
    try {
      const [subResponse, plansResponse] = await Promise.all([
        subscriptionsApi.getCurrentSubscription(),
        subscriptionsApi.getPlans(),
      ]);
      setSubscription(subResponse.subscription);
      setCurrentPlanName(subResponse.subscription?.plan_name);
      setIsTrial(isInTrial(subResponse.subscription));
      setPlans(plansResponse.plans);
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
    }
  };

  useEffect(() => {
    refreshSubscription().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('topup_success') === 'true') {
      setNotification({
        type: 'success',
        message: 'Top-up purchased successfully! It will be added to your account shortly.'
      });
      window.history.replaceState({}, '', '/subscriptions');
    } else if (searchParams.get('topup_canceled') === 'true') {
      setNotification({
        type: 'error',
        message: 'Top-up purchase was canceled. No charges were made.'
      });
      window.history.replaceState({}, '', '/subscriptions');
    } else if (searchParams.get('unsubscribe_success') === 'true') {
      setNotification({
        type: 'success',
        message: "You're unsubscribed. You'll keep your current plan until your billing period ends."
      });
      window.history.replaceState({}, '', '/subscriptions');
    }

    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo === 'plans' && !loading) {
      setTimeout(() => {
        subscriptionPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState({}, '', '/subscriptions');
      }, 300);
    }
  }, [location, loading]);

  // Auto-open checkout when arriving from a lifecycle email deep link (?plan=starter|growth)
  useEffect(() => {
    if (loading) return;

    const requestedPlan = new URLSearchParams(location.search).get('plan')?.toLowerCase();
    if (!requestedPlan || !['starter', 'growth', 'scale'].includes(requestedPlan)) return;

    // Already on the requested plan: just scroll to plans instead of charging again
    if (currentPlanName?.toLowerCase() === requestedPlan) {
      window.history.replaceState({}, '', '/subscriptions');
      subscriptionPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    let cancelled = false;
    const startCheckout = async () => {
      try {
        const { plans } = await subscriptionsApi.getPlans();
        const match = plans?.find(
          (p: { name: string; id: number }) => p.name?.toLowerCase() === requestedPlan
        );
        window.history.replaceState({}, '', '/subscriptions');
        if (!cancelled && match) {
          const hasLiveSubscription = !!currentPlanName && currentPlanName !== 'discover';
          if (hasLiveSubscription) {
            openPlanSwitch(match.id);
          } else {
            handleSelectPlan(match.id);
          }
        } else if (!cancelled) {
          subscriptionPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('Failed to start checkout from email link:', error);
      }
    };

    startCheckout();
    return () => {
      cancelled = true;
    };
  }, [location, loading, currentPlanName]);

  const handleSelectPlan = async (planId: number) => {
    setRedirectingToPlan(true);
    try {
      const successUrl = `${window.location.origin}/subscriptions?success=true`;
      const cancelUrl = `${window.location.origin}/subscriptions?canceled=true`;

      const response = await subscriptionsApi.createCheckoutSession(
        planId,
        successUrl,
        cancelUrl
      );

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      // No redirect URL came back — don't leave the overlay spinning silently.
      setRedirectingToPlan(false);
      setNotification({
        type: 'error',
        message: "We couldn't start checkout. Please try again.",
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setRedirectingToPlan(false);

      const data = axios.isAxiosError(error)
        ? (error.response?.data as { detail?: string } | undefined)
        : undefined;
      const detail = typeof data?.detail === 'string' ? data.detail : undefined;

      setNotification({
        type: 'error',
        message: detail ?? "We couldn't start checkout. Please try again.",
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openPlanSwitch = (planId: number) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan) setPlanSwitchTarget(plan);
  };

  const handleConfirmPlanSwitch = async () => {
    if (!planSwitchTarget) return;
    try {
      const result = await subscriptionsApi.changePlan(planSwitchTarget.id);
      posthog?.capture('plan_switch', {
        from_plan: subscription?.plan_name,
        to_plan: result.subscription.plan_name,
        direction: result.direction,
        was_trial: isTrial,
      });
      setPlanSwitchTarget(null);
      setNotification({
        type: 'success',
        message: `You're now on the ${result.subscription.plan_display_name} plan.`,
      });
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to change plan:', error);
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 402) {
        setNotification({
          type: 'error',
          message: "We couldn't process your payment. Please update your payment method and try again.",
        });
      } else {
        const data = axios.isAxiosError(error)
          ? (error.response?.data as { detail?: string } | undefined)
          : undefined;
        setNotification({
          type: 'error',
          message: data?.detail ?? "We couldn't switch your plan. Please try again.",
        });
      }
    }
  };

  const handleUnsubscribeSuccess = async () => {
    await refreshSubscription();
    setNotification({
      type: 'success',
      message: "You're unsubscribed. You'll keep your current plan until your billing period ends.",
    });
  };

  const handleReactivate = async () => {
    try {
      await subscriptionsApi.reactivateSubscription();
      setNotification({ type: 'success', message: 'Your subscription has been reactivated.' });
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      setNotification({ type: 'error', message: 'Failed to reactivate. Please try again.' });
    }
  };

  const handleCancelTrial = async () => {
    try {
      await subscriptionsApi.cancelSubscription();
      setNotification({
        type: 'success',
        message: `Your trial will end on schedule and you won't be charged.`,
      });
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to cancel trial:', error);
      setNotification({ type: 'error', message: 'Failed to cancel trial. Please try again.' });
    }
  };

  const handleManageBilling = async () => {
    try {
      const { portal_url } = await subscriptionsApi.createPortalSession(`${window.location.origin}/subscriptions`);
      if (portal_url) window.location.href = portal_url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      setNotification({ type: 'error', message: 'Failed to open billing portal. Please try again.' });
    }
  };

  return (
    <PageContainer
      title="Subscription Plans"
      titleIcon={<CreditCard />}
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">

        {/* Notification */}
        {notification && (
          <div className="mb-3 sm:mb-6">
            <Notification
              type={notification.type}
              message={notification.message}
              onClose={() => setNotification(null)}
            />
          </div>
        )}

        <p className="mb-4 max-w-2xl text-sm text-gray-600 sm:mb-6 sm:text-base">
          Manage your plan, quotas, and recent usage.
        </p>

        {/* Trial status */}
        {!loading && isTrial && subscription && (
          <TrialStatusCard
            subscription={subscription}
            currentPlan={plans.find((p) => p.name === subscription.plan_name)}
            starterPlan={plans.find((p) => p.name === 'starter')}
            onSwitchToStarter={() => {
              const starterPlan = plans.find((p) => p.name === 'starter');
              if (starterPlan) openPlanSwitch(starterPlan.id);
            }}
            onScrollToPlans={() => subscriptionPlansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            onManageBilling={handleManageBilling}
            onCancelConfirmed={handleCancelTrial}
          />
        )}

        {/* Billing cycle status for active, non-trial paid subscribers */}
        {!loading && !isTrial && subscription && subscription.plan_name !== 'discover' && (
          <BillingCycleCard
            subscription={subscription}
            currentPlan={plans.find((p) => p.name === subscription.plan_name)}
            onManageBilling={handleManageBilling}
            onReactivate={handleReactivate}
          />
        )}

        {/* Quotas */}
        <div className="mb-4 sm:mb-8 lg:mb-12">
          <Card>
            <div className="p-3 sm:p-6">
              <h3 className="mb-3 text-lg font-semibold text-gray-900 sm:mb-4">Your Quotas</h3>
              <CreditBalance />
            </div>
          </Card>
        </div>

        {/* Plans */}
        {!loading && (
          <div ref={subscriptionPlansRef} className="mb-4 scroll-mt-20 sm:mb-8 lg:mb-12">
            <SubscriptionPlans
              currentPlanName={currentPlanName}
              isTrial={isTrial}
              cancelAtPeriodEnd={subscription?.cancel_at_period_end}
              currentPeriodEnd={subscription?.current_period_end}
              onSelectPlan={handleSelectPlan}
              onUnsubscribeSuccess={handleUnsubscribeSuccess}
              onRequestPlanSwitch={openPlanSwitch}
            />
          </div>
        )}

        {/* Usage history */}
        <div className="mb-4 sm:mb-8 lg:mb-12">
          <CreditHistory />
        </div>
      </div>

      {redirectingToPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
          <div className="text-center">
            <div className="inline-block animate-bounce text-6xl mb-4">
              🚀
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Redirecting to checkout...
            </h3>
            <p className="text-gray-600">
              Please wait while we prepare your payment
            </p>
          </div>
        </div>
      )}

      {planSwitchTarget && (() => {
        const currentPlan = plans.find((p) => p.name === currentPlanName);
        const direction = currentPlan && planSwitchTarget.price_cents < currentPlan.price_cents
          ? 'downgrade'
          : 'upgrade';
        return (
          <PlanSwitchModal
            isOpen={!!planSwitchTarget}
            onClose={() => setPlanSwitchTarget(null)}
            onConfirm={handleConfirmPlanSwitch}
            currentPlan={currentPlan}
            targetPlan={planSwitchTarget}
            direction={direction}
            isTrial={isTrial}
            trialEndLabel={
              subscription?.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : undefined
            }
          />
        );
      })()}
    </PageContainer>
  );
};

export default Subscriptions;
